import {
  bit,
  defaultSource,
  flag,
  oneOf,
  readSheetSource,
  type SheetSourceState,
  writeSheetSource,
} from "./worksheet-state.js";

/**
 * A pinyin sheet's settings, and where they live in the address.
 *
 * The same fragment the character sheet keeps its settings in, for the same
 * reasons: a typed list can run past what CloudFront answers, and a link has
 * to print the same sheet again. Where the words come from and the paper are
 * read by `worksheet-state.ts`, so a List's link opens either sheet on it.
 */

/** Which half of a syllable the initials and finals part asks for. */
export type PinyinGap = "mixed" | "initials" | "finals";

/** Everything a pinyin sheet is drawn from. */
export interface PinyinWorksheetState extends SheetSourceState {
  /** Whether the sheet asks for the tone marks on numbered pinyin. */
  readonly tones: boolean;

  /** Whether the sheet asks for the pinyin of each word's 汉字. */
  readonly pinyin: boolean;

  /** Whether the sheet asks for the missing initial or final of syllables. */
  readonly split: boolean;

  /**
   * Which half of each syllable the split part leaves out: either, chosen
   * from the seed syllable by syllable, or always the one. A syllable with no
   * initial is asked its final whatever this says.
   */
  readonly gap: PinyinGap;

  /** Whether the sheet asks for the 汉字 of each word's pinyin. */
  readonly hanzi: boolean;

  /** Whether the sheet has a page of empty four-line pinyin paper. */
  readonly lines: boolean;

  /** Whether the sheet ends with its answers. */
  readonly key: boolean;
}

/** A pinyin sheet nobody has touched. */
export const defaultPinyinState: PinyinWorksheetState = {
  ...defaultSource,
  tones: true,
  pinyin: true,
  split: false,
  gap: "mixed",
  hanzi: false,
  lines: false,
  key: true,
};

/** The tick boxes, in the order the sheet prints their parts. */
const parts = ["tones", "pinyin", "split", "hanzi", "lines", "key"] as const;

/**
 * Read a pinyin sheet's settings out of an address fragment.
 * @param hash - `location.hash`, with or without its `#`.
 * @returns The settings, with the defaults wherever the fragment said nothing
 * usable.
 */
export function readPinyinWorksheetState(hash: string): PinyinWorksheetState {
  const parameters = new URLSearchParams(hash.replace(/^#/u, ""));
  const d = defaultPinyinState;

  return {
    ...readSheetSource(parameters),
    tones: flag(parameters.get("tones"), d.tones),
    pinyin: flag(parameters.get("pinyin"), d.pinyin),
    split: flag(parameters.get("split"), d.split),
    gap: oneOf(parameters.get("gap"), ["mixed", "initials", "finals"], d.gap),
    hanzi: flag(parameters.get("hanzi"), d.hanzi),
    lines: flag(parameters.get("lines"), d.lines),
    key: flag(parameters.get("key"), d.key),
  };
}

/**
 * The fragment for a pinyin sheet, holding only what differs from the
 * defaults.
 * @returns The fragment without its `#`, empty for an untouched sheet.
 */
export function pinyinWorksheetFragment(state: PinyinWorksheetState): string {
  const parameters = new URLSearchParams();
  writeSheetSource(parameters, state);
  for (const part of parts) {
    if (state[part] !== defaultPinyinState[part]) {
      parameters.set(part, bit(state[part]));
    }
  }
  // Which half is left out only matters to a sheet that leaves one out.
  if (state.split && state.gap !== defaultPinyinState.gap) {
    parameters.set("gap", state.gap);
  }

  return parameters.toString();
}
